import { useCallback, useEffect, useRef, useState } from 'react';

import { Autocomplete } from '@/components/Inputs';

export const AddressInput = ({
  initialInput,
  onPlaceChange,
  className,
  label,
  placeholder,
  searchOptions,
}: {
  initialInput: string;
  onPlaceChange(place: null | PlaceDetails): void;
  className?: string;
  label: string;
  placeholder?: string;
  searchOptions?: PredictionOptions;
}) => {
  const [place, setPlace] = useState<null | PlaceDetails>(null);

  const handleLocationChange = useCallback(
    (place: null | PlaceDetails) => {
      setPlace(place);
      onPlaceChange(place);
    },
    [onPlaceChange],
  );

  return (
    <Address
      place={place}
      onPlaceChange={handleLocationChange}
      initialInput={initialInput}
      className={className}
      label={label}
      placeholder={placeholder}
      searchOptions={searchOptions}
    />
  );
};

const Address = ({
  place,
  onPlaceChange,
  initialInput,
  className,
  label,
  placeholder,
  searchOptions,
}: {
  place: null | PlaceDetails;
  onPlaceChange: (place: null | PlaceDetails) => void;
  initialInput?: string;
  className?: string;
  label: string;
  placeholder?: string;
  searchOptions?: PredictionOptions;
}) => {
  const [input, setInput] = useState(initialInput ?? '');
  const [predictions, setPredictions] = useState<null | AutocompletePrediction[]>(null);
  const initialInputRef = useRef(initialInput);
  const searchOptionsRef = useRef(searchOptions);
  // Ref the callback so the mount effect stays mount-only even when the parent
  // passes a fresh onPlaceChange each render — otherwise it loops (spams Places).
  const handlePlaceClickRef = useRef<(placeId: null | string) => void>(() => {});

  const handleInput = async (input: string) => {
    setInput(input);
    onPlaceChange(null);
    setPredictions(null);
    try {
      const predictions = await fetchAutocompletePredictions(input, searchOptions);
      setPredictions(predictions);
    } catch (err: unknown) {
      console.error(err);
    }
  };

  const handlePlaceClick = useCallback(
    async (placeId: null | string) => {
      try {
        const place = placeId ? await fetchPlaceDetails(placeId) : null;
        if (place) {
          onPlaceChange(place);
        } else {
          onPlaceChange(null);
        }
      } catch (err: unknown) {
        console.error(err);
      }
    },
    [onPlaceChange],
  );

  useEffect(() => {
    handlePlaceClickRef.current = handlePlaceClick;
  });

  useEffect(() => {
    const initialInputCurrent = initialInputRef.current;
    if (!initialInputCurrent) {
      return;
    }
    (async () => {
      const [prediction] = await fetchAutocompletePredictions(
        initialInputCurrent,
        searchOptionsRef.current,
      );
      if (prediction) {
        handlePlaceClickRef.current(prediction.placeId);
      }
    })();
  }, []);

  return (
    <Autocomplete
      label={label}
      placeholder={placeholder ?? label}
      options={
        predictions?.map(({ description, placeId }) => ({ label: description, value: placeId })) ??
        []
      }
      className={className}
      onChange={handleInput}
      value={input}
      selectedOptionLabel={place?.formattedAddress}
      onItemSelect={handlePlaceClick}
    />
  );
};

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

export interface AutocompletePrediction {
  placeId: string;
  description: string;
}

/** Google Places autocomplete filters. Defaults to US localities (city search). */
export interface PredictionOptions {
  includedPrimaryTypes?: string[];
  includedRegionCodes?: string[];
}

export interface PlaceDetails {
  id: string;
  displayName?: {
    text: string;
  };
  formattedAddress?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  addressComponents?: Array<{
    types?: string[];
    longText?: string;
    shortText?: string;
    text?: string;
  }>;
}

const fetchAutocompletePredictions = async (
  input: string,
  options?: PredictionOptions,
): Promise<AutocompletePrediction[]> => {
  if (!input || input.length < 3) {
    return [];
  }

  // Default = US localities (the city picker). Callers pass options to widen the search,
  // e.g. global street addresses for "where do you operate". Region filter is omitted when empty.
  const includedPrimaryTypes = options?.includedPrimaryTypes ?? ['locality'];
  const includedRegionCodes = options ? options.includedRegionCodes : ['us'];

  try {
    const response = await fetch(`https://places.googleapis.com/v1/places:autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
        'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
      },
      body: JSON.stringify({
        input,
        includedPrimaryTypes,
        ...(includedRegionCodes?.length ? { includedRegionCodes } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(`Places API error: ${response.status}`);
    }

    const data = await response.json();
    const suggestions =
      data.suggestions?.map(
        (s: { placePrediction: { placeId: string; text: { text: string } } }) => ({
          placeId: s.placePrediction.placeId,
          description: s.placePrediction.text.text,
        }),
      ) || [];

    return suggestions;
  } catch (error) {
    console.error('Error fetching autocomplete predictions:', error);
    return [];
  }
};

const fetchPlaceDetails = async (placeId: string): Promise<PlaceDetails | null> => {
  try {
    const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
        'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,addressComponents',
      },
    });

    if (!response.ok) {
      throw new Error(`Places API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching place details:', error);
    return null;
  }
};
